import {RefObject, useEffect, useRef} from 'react';

const threshold = 0.5;
// Mobile fixed header overlays the top of the viewport, so subtract its height from the visible area.
const mobileFixedHeaderHeightPx = 52;

/**
 * Tracks whether the banner is visible enough for refresh actions.
 * The current state is kept in a ref to avoid re-subscribing to refresh events.
 */
export function useBannerViewability(
	targetRef: RefObject<Element>,
	interfaceName: string,
	deps?: unknown[],
) {
	const isViewableEnoughRef = useRef(false);

	useEffect(() => {
		if (!targetRef.current) {
			return;
		}

		if (!window.IntersectionObserver) {
			isViewableEnoughRef.current = true;
			return;
		}

		const rootMargin = interfaceName === 'mobile' ? `-${mobileFixedHeaderHeightPx}px 0px 0px 0px` : undefined;

		const observer = new IntersectionObserver(
			([entry]) => {
				isViewableEnoughRef.current = entry.intersectionRatio >= threshold;
			},
			{
				threshold,
				rootMargin,
			},
		);

		observer.observe(targetRef.current);

		return () => {
			observer.disconnect();
		};
	}, [threshold, interfaceName, ...(deps || [])]);

	return isViewableEnoughRef;
}

useBannerViewability.displayName = 'useBannerViewability';
